import {
  hasPermission,
  okSchema,
  playerActionResultSchema,
  playerNameSchema,
  rconSetupResultSchema,
  serverLiveSchema,
  steamIdSchema,
  type PlayerAction,
  type ServerLive,
} from '@hearth/shared';
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import {
  Ban,
  CircleOff,
  KeyRound,
  Loader2,
  LogOut,
  MoreHorizontal,
  RotateCw,
  Shield,
  ShieldOff,
  Unplug,
  UserCheck,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { FormField } from '@/components/form-field';
import { EmptyState } from '@/components/page-header';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { apiRequest } from '@/lib/api';
import { errorMessage } from '@/lib/forms';
import { serverQuery } from '@/lib/server-queries';

export const Route = createFileRoute('/_app/server/$server/players')({ component: PlayersTab });

const liveQuery = (server: string) =>
  queryOptions({
    queryKey: ['server', server, 'live'],
    queryFn: ({ signal }) =>
      apiRequest(`/api/client/servers/${server}/live`, { schema: serverLiveSchema, signal }),
    // hearthd caches for 5 s, so polling faster than this gains nothing.
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  });

const LABELS: Record<PlayerAction, string> = {
  kick: 'Kick',
  ban: 'Ban',
  pardon: 'Unban',
  op: 'Make operator',
  deop: 'Remove operator',
  'whitelist-add': 'Add to whitelist',
  'whitelist-remove': 'Remove from whitelist',
  unban: 'Unban',
};

/** Actions that ask for a reason first. */
const WITH_REASON = new Set<PlayerAction>(['kick', 'ban']);

function PlayersTab() {
  const { server: id } = Route.useParams();
  const { data: server } = useSuspenseQuery(serverQuery(id));
  const live = useQuery(liveQuery(id));
  const queryClient = useQueryClient();
  const can = (p: Parameters<typeof hasPermission>[1]) => hasPermission(server.permissions, p);
  const canAct = can('player.manage');
  const [asking, setAsking] = useState<{ action: 'kick' | 'ban'; player: string } | null>(null);

  const act = useMutation({
    mutationFn: ({
      action,
      player,
      reason,
    }: {
      action: PlayerAction;
      player: string;
      reason?: string;
    }) =>
      apiRequest(`/api/client/servers/${id}/players/${encodeURIComponent(player)}/${action}`, {
        method: 'POST',
        body: reason ? { reason } : {},
        schema: playerActionResultSchema,
      }),
    onSuccess: (res) => {
      // The game's own words, e.g. "Made Alex a server operator".
      toast.success(res.reply || 'Done.');
      setAsking(null);
      void queryClient.invalidateQueries({ queryKey: liveQuery(id).queryKey });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const run = (action: PlayerAction, player: string) => {
    if (action === 'kick' || action === 'ban') setAsking({ action, player });
    else act.mutate({ action, player });
  };
  const nameOf = (player: string) =>
    live.data?.roster?.find((p) => p.id === player)?.name ?? player;

  if (live.isPending)
    return (
      <div className="grid gap-3" aria-busy>
        <Skeleton className="h-20 rounded-xl" />
        <Skeleton className="h-48 rounded-xl" />
      </div>
    );
  if (live.isError)
    return (
      <Alert variant="destructive">
        <Unplug />
        <AlertTitle>Couldn’t load players</AlertTitle>
        <AlertDescription>{errorMessage(live.error)}</AlertDescription>
      </Alert>
    );

  const data = live.data;
  if (data.status === 'unsupported')
    return (
      <EmptyState icon={CircleOff} title="Not available for this game">
        Hearth can list and moderate players on Minecraft: Java Edition and Rust servers so far.
      </EmptyState>
    );
  const rconReady = data.rcon === 'ready' && data.status === 'ready';
  // Rust without RCON has nothing else to ask; the notice above says what to set.
  const waitingForSetup = data.setup === 'startup' && data.rcon === 'not_configured';

  return (
    <div className="grid gap-4">
      <Summary live={data} />
      <RconNotice
        server={id}
        live={data}
        canSetup={can('config.update')}
        canRestart={can('control.restart')}
        canSeeStartup={can('startup.read')}
      />

      {data.status === 'offline' ? (
        <EmptyState icon={Users} title="The server is offline">
          Start it to see who’s online. You can still manage players by name below once it runs.
        </EmptyState>
      ) : waitingForSetup ? null : data.status === 'unreachable' ? (
        <Alert>
          <Unplug />
          <AlertTitle>The game isn’t answering yet</AlertTitle>
          <AlertDescription>
            {data.detail ?? 'It may still be starting.'} This page refreshes on its own.
          </AlertDescription>
        </Alert>
      ) : (
        <Card className="gap-0 overflow-hidden p-0">
          <div className="flex items-center justify-between border-b px-4 py-3">
            <h2 className="font-medium">Online now</h2>
            {!data.playersComplete && data.online > 0 && (
              <p className="text-muted-foreground text-xs">
                From the status ping, which may show only some players.
              </p>
            )}
          </div>
          {data.roster ? (
            data.roster.length === 0 ? (
              <p className="text-muted-foreground px-4 py-8 text-center text-sm">
                Nobody is online.
              </p>
            ) : (
              <ul className="divide-y">
                {data.roster.map((p) => (
                  <li key={p.id} className="flex items-center gap-3 px-4 py-2.5">
                    <span
                      aria-hidden
                      className="bg-game-soft text-game grid size-8 place-items-center rounded-md text-xs font-semibold uppercase"
                    >
                      {p.name.slice(0, 2)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">{p.name}</p>
                      <p className="text-muted-foreground font-mono text-xs">{p.id}</p>
                    </div>
                    <span className="text-muted-foreground hidden text-xs tabular-nums sm:inline">
                      {p.pingMs} ms · {duration(p.connectedSeconds)}
                    </span>
                    {canAct && rconReady && (
                      <PlayerMenu
                        name={p.name}
                        actions={data.actions}
                        onAction={(a) => run(a, p.id)}
                        busy={act.isPending}
                      />
                    )}
                  </li>
                ))}
              </ul>
            )
          ) : data.players.length === 0 ? (
            <p className="text-muted-foreground px-4 py-8 text-center text-sm">
              {data.online > 0
                ? `${data.online} online, but the server hides their names. Turn on RCON to see them.`
                : 'Nobody is online.'}
            </p>
          ) : (
            <ul className="divide-y">
              {data.players.map((name) => (
                <li key={name} className="flex items-center gap-3 px-4 py-2.5">
                  <span
                    aria-hidden
                    className="bg-game-soft text-game grid size-8 place-items-center rounded-md text-xs font-semibold uppercase"
                  >
                    {name.replace(/^\./, '').slice(0, 2)}
                  </span>
                  <span className="min-w-0 flex-1 truncate font-medium">{name}</span>
                  {canAct && rconReady && (
                    <PlayerMenu
                      name={name}
                      actions={data.actions}
                      onAction={(a) => run(a, name)}
                      busy={act.isPending}
                    />
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {canAct && rconReady && (
        <ByName
          bySteamId={data.adapter === 'rust'}
          actions={data.actions}
          onAction={(action, player) => run(action, player)}
          busy={act.isPending}
        />
      )}

      {asking && (
        <ReasonDialog
          action={asking.action}
          player={nameOf(asking.player)}
          busy={act.isPending}
          onClose={() => setAsking(null)}
          onConfirm={(reason) => act.mutate({ ...asking, reason })}
        />
      )}
    </div>
  );
}

function Summary({ live }: { live: ServerLive }) {
  const up = live.status === 'ready';
  return (
    <Card className="flex-row flex-wrap items-center gap-x-8 gap-y-2 px-4 py-3">
      <div>
        <p className="text-muted-foreground text-xs">Players</p>
        <p className="text-xl font-semibold tabular-nums">
          {up ? (
            <>
              {live.online}
              <span className="text-muted-foreground text-sm font-normal"> / {live.max}</span>
            </>
          ) : (
            '—'
          )}
        </p>
      </div>
      {live.version && (
        <div className="min-w-0">
          <p className="text-muted-foreground text-xs">Version</p>
          <p className="truncate font-medium">{live.version}</p>
        </div>
      )}
      {up && live.queued !== null && live.queued > 0 && (
        <div>
          <p className="text-muted-foreground text-xs">Queued</p>
          <p className="text-xl font-semibold tabular-nums">{live.queued}</p>
        </div>
      )}
      {live.motd && (
        <div className="min-w-0 flex-1">
          <p className="text-muted-foreground text-xs">
            {live.adapter === 'rust' ? 'Server name' : 'Message of the day'}
          </p>
          <p className="truncate">{live.motd}</p>
        </div>
      )}
    </Card>
  );
}

/** What RCON needs, as a setup step with a real button; nothing is changed without a click. */
function RconNotice({
  server,
  live,
  canSetup,
  canRestart,
  canSeeStartup,
}: {
  server: string;
  live: ServerLive;
  canSetup: boolean;
  canRestart: boolean;
  canSeeStartup: boolean;
}) {
  const queryClient = useQueryClient();
  const setup = useMutation({
    mutationFn: () =>
      apiRequest(`/api/client/servers/${server}/live/rcon`, {
        method: 'POST',
        schema: rconSetupResultSchema,
      }),
    onSuccess: (r) => {
      toast.success(
        r.restartRequired ? 'RCON is on. Restart the server to use it.' : 'RCON was already on.',
      );
      void queryClient.invalidateQueries({ queryKey: liveQuery(server).queryKey });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const restart = useMutation({
    mutationFn: () =>
      apiRequest(`/api/client/servers/${server}/power`, {
        method: 'POST',
        body: { action: 'restart' },
        schema: okSchema,
      }),
    onSuccess: () => toast.success('Restarting…'),
    onError: (e) => toast.error(errorMessage(e)),
  });

  const fromStartup = live.setup === 'startup';
  if (live.rcon === 'not_configured' && fromStartup)
    return (
      <Alert>
        <KeyRound />
        <AlertTitle>Set up RCON to see and moderate players</AlertTitle>
        <AlertDescription className="grid gap-3">
          <p>
            This game’s RCON comes from its startup variables, which Hearth won’t change for you.
            {live.missing.length > 0 && (
              <>
                {' '}
                Missing or invalid:{' '}
                {live.missing.map((m, i) => (
                  <span key={m}>
                    {i > 0 && ', '}
                    <code className="font-mono">{m}</code>
                  </span>
                ))}
                .
              </>
            )}{' '}
            Hearth connects only from this node; the RCON port is never opened to the internet.
          </p>
          {canSeeStartup ? (
            <Button asChild variant="outline" className="w-fit">
              <Link to="/server/$server/startup" params={{ server }}>
                Open Startup
              </Link>
            </Button>
          ) : (
            <p>Ask the server’s owner to set it on the Startup tab.</p>
          )}
        </AlertDescription>
      </Alert>
    );
  if (live.rcon === 'not_configured')
    return (
      <Alert>
        <KeyRound />
        <AlertTitle>Turn on RCON to moderate players</AlertTitle>
        <AlertDescription className="grid gap-3">
          <p>
            RCON lets Hearth list every player and kick, ban or op them. Hearth turns it on in
            server.properties with a generated password and only connects from this node; the port
            is never opened to the internet.
          </p>
          {canSetup ? (
            <Button className="w-fit" onClick={() => setup.mutate()} disabled={setup.isPending}>
              {setup.isPending && <Loader2 className="animate-spin" />} Turn on RCON
            </Button>
          ) : (
            <p>Ask the server’s owner to turn it on (it needs permission to edit files).</p>
          )}
        </AlertDescription>
      </Alert>
    );
  if (live.rcon === 'restart_required')
    return (
      <Alert>
        <RotateCw />
        <AlertTitle>Restart to finish turning on RCON</AlertTitle>
        <AlertDescription className="grid gap-3">
          <p>
            {fromStartup
              ? 'RCON was set after the server started; it takes effect on the next start.'
              : 'The game reads server.properties when it starts.'}
          </p>
          {canRestart && (
            <Button
              variant="outline"
              className="w-fit"
              onClick={() => restart.mutate()}
              disabled={restart.isPending}
            >
              <RotateCw /> Restart now
            </Button>
          )}
        </AlertDescription>
      </Alert>
    );
  if (live.rcon === 'unreachable' && (live.status === 'ready' || fromStartup))
    return (
      <Alert variant="destructive">
        <Unplug />
        <AlertTitle>RCON isn’t answering</AlertTitle>
        <AlertDescription>
          {fromStartup
            ? `${live.detail ?? 'The server may still be starting.'} If you changed RCON_PASS or RCON_PORT, restart the server.`
            : 'The player list may be incomplete and actions won’t work. If you changed rcon.password or rcon.port, restart the server.'}
        </AlertDescription>
      </Alert>
    );
  return null;
}

function PlayerMenu({
  name,
  actions,
  onAction,
  busy,
}: {
  name: string;
  actions: PlayerAction[];
  onAction: (a: PlayerAction) => void;
  busy: boolean;
}) {
  const has = (a: PlayerAction) => actions.includes(a);
  const grants = (['op', 'deop', 'whitelist-add'] as const).filter(has);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${name}`} disabled={busy}>
          <MoreHorizontal />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {has('op') && (
          <DropdownMenuItem onSelect={() => onAction('op')}>
            <Shield /> {LABELS.op}
          </DropdownMenuItem>
        )}
        {has('deop') && (
          <DropdownMenuItem onSelect={() => onAction('deop')}>
            <ShieldOff /> {LABELS.deop}
          </DropdownMenuItem>
        )}
        {has('whitelist-add') && (
          <DropdownMenuItem onSelect={() => onAction('whitelist-add')}>
            <UserCheck /> {LABELS['whitelist-add']}
          </DropdownMenuItem>
        )}
        {grants.length > 0 && <DropdownMenuSeparator />}
        {has('kick') && (
          <DropdownMenuItem onSelect={() => onAction('kick')}>
            <LogOut /> Kick…
          </DropdownMenuItem>
        )}
        {has('ban') && (
          <DropdownMenuItem className="text-destructive" onSelect={() => onAction('ban')}>
            <Ban /> Ban…
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * For players who aren't online: unban, whitelist, op by name (Minecraft) or by Steam ID
 * (Rust, whose display names can't safely be put in a command).
 */
function ByName({
  bySteamId,
  actions,
  onAction,
  busy,
}: {
  bySteamId: boolean;
  actions: PlayerAction[];
  onAction: (a: PlayerAction, player: string) => void;
  busy: boolean;
}) {
  const [name, setName] = useState('');
  const offered = actions.filter((a) => a !== 'kick');
  const [action, setAction] = useState<PlayerAction>(
    offered.includes('whitelist-add') ? 'whitelist-add' : (offered[0] ?? 'ban'),
  );
  const valid = (bySteamId ? steamIdSchema : playerNameSchema).safeParse(name).success;
  if (offered.length === 0) return null;
  return (
    <Card className="gap-3 px-4 py-4">
      <h2 className="font-medium">
        {bySteamId ? 'Manage a player by Steam ID' : 'Manage a player by name'}
      </h2>
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (valid) onAction(action, name);
        }}
      >
        <div className="w-full sm:w-56">
          {bySteamId ? (
            <FormField
              label="Steam ID"
              error={name && !valid ? 'A 17-digit SteamID64, starting 7656119.' : undefined}
            >
              <Input
                value={name}
                onChange={(e) => setName(e.target.value.trim())}
                inputMode="numeric"
                maxLength={17}
                className="font-mono"
              />
            </FormField>
          ) : (
            <FormField
              label="Player name"
              error={name && !valid ? 'Letters, digits and _ only, up to 16.' : undefined}
            >
              <Input value={name} onChange={(e) => setName(e.target.value.trim())} maxLength={17} />
            </FormField>
          )}
        </div>
        <Select value={action} onValueChange={(v) => setAction(v as PlayerAction)}>
          <SelectTrigger className="w-full sm:w-56" aria-label="Action">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {offered.map((a) => (
              <SelectItem key={a} value={a}>
                {WITH_REASON.has(a) ? `${LABELS[a]}…` : LABELS[a]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button type="submit" disabled={!valid || busy}>
          Apply
        </Button>
      </form>
    </Card>
  );
}

function ReasonDialog({
  action,
  player,
  busy,
  onClose,
  onConfirm,
}: {
  action: 'kick' | 'ban';
  player: string;
  busy: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {action === 'ban' ? 'Ban' : 'Kick'} {player}?
          </DialogTitle>
          <DialogDescription>
            {action === 'ban'
              ? 'They are disconnected and can’t rejoin until you unban them.'
              : 'They are disconnected and can rejoin straight away.'}
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            onConfirm(reason);
          }}
        >
          <FormField label="Reason (shown to the player)">
            <Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={200} />
          </FormField>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant={action === 'ban' ? 'destructive' : 'default'}
              disabled={busy}
            >
              {action === 'ban' ? 'Ban' : 'Kick'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** "2h 5m" style, for how long a Rust player has been connected. */
function duration(seconds: number) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m`;
  return `${seconds}s`;
}
